import { lockedApi } from "@/api";
import { AxiosInstance, AxiosStatic } from "axios";
import { useMergeTagsStore } from "@/stores/mergeTags";
import { useFontStore } from "@/stores/fonts";
import { useGeneralStore } from "@/stores/generalStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useTemplateSizingStore } from "@/stores/templateSizingStore";
import { usePreviewStore } from "@/stores/preview";
import { useNotificationsStore } from "@/stores/notifications";
import { ref, getCurrentInstance } from "vue";
import { ISetPreviewParams } from "types/Preview/Preview";

export default () => {
  const app = getCurrentInstance();

  if (app === null) {
    throw new Error("App is not defined");
  }

  const $http: AxiosStatic | AxiosInstance =
    app.appContext.config.globalProperties.$http;

  const mergeTagsStore = useMergeTagsStore();
  const fontStore = useFontStore();
  const generalStore = useGeneralStore();
  const mjmlStore = useMjmlStore();
  const templateSizingStore = useTemplateSizingStore();
  const previewStore = usePreviewStore();
  const notificationsStore = useNotificationsStore();

  const isLoading = ref(true);
  const previewTemplate = ref("");
  const tepmlateSize = ref(0);

  // async function getSizeByUrl(url: string) {
  //   const response = await $http.get(url, {
  //     headers: {
  //       Accept: "*/*",
  //       "x-goog-acl": "public-read",
  //     },
  //   });
  //   if (response && response.headers["content-length"]) {
  //     tepmlateSize.value += response.headers["content-length"];
  //   }
  // }

  function getImageRatioIframe() {
    let imageContent = 0;
    let noImageContent = 0;

    const iframe = document.getElementById(
      "previewIframe"
    ) as HTMLIFrameElement;
    try {
      if (
        iframe &&
        iframe.contentWindow &&
        iframe.contentWindow.document.getElementsByTagName("body")[0]
      ) {
        const allImages =
          iframe.contentWindow.document.getElementsByTagName("img");
        const allColumns = iframe.contentWindow.document.querySelectorAll(
          '[class^="mj-column-per-"]>table>tbody>tr>td'
        );
        const bgElement = iframe.contentWindow.document.querySelectorAll(
          'table[style^="background:url("]'
        );

        for (const column of allColumns) {
          if (column.querySelector("img")) {
            continue;
          }
          let heightColumn = column.clientHeight;
          let widthColumn = column.clientWidth;
          heightColumn -=
            parseFloat(getComputedStyle(column).paddingTop) +
            parseFloat(getComputedStyle(column).paddingBottom);
          widthColumn -=
            parseFloat(getComputedStyle(column).paddingLeft) +
            parseFloat(getComputedStyle(column).paddingRight);
          noImageContent += heightColumn * widthColumn;
        }

        for (const image of allImages) {
          imageContent += image.clientHeight * image.clientWidth;
          // await getSizeByUrl(image.src);
        }

        for (const bg of bgElement) {
          imageContent += bg.clientHeight * bg.clientWidth;
        }

        templateSizingStore.imageContent = imageContent;
        templateSizingStore.nonImageContent = noImageContent;
      }
    } catch (e) {
      console.error(e);
      templateSizingStore.imageContent = NaN;
      templateSizingStore.nonImageContent = NaN;
    }
    previewStore.isRatioLoading = false;
  }

  const setPreview = async (params?: ISetPreviewParams) => {
    //Change URL for backwards compat
    //Vue resource was able to use wildcard in url like preview/{key}
    //axios can't do that, so fix is needed to here for the api to work like it used to
    isLoading.value = true;
    previewStore.isRatioLoading = true;
    previewStore.iframeIsLoading = true;
    previewStore.params = params;
    tepmlateSize.value = 0;

    try {
      const data = await getPreview(
        JSON.stringify(mjmlStore.getMjml()),
        params
      );

      const byteSize = (str: string) => new Blob([str]).size;

      if (data.headers["content-length"]) {
        tepmlateSize.value += data.headers["content-length"] as number;
      } else {
        tepmlateSize.value += byteSize(data.data?.html);
      }

      if (data.data?.html) {
        previewTemplate.value = data.data.html;
      }

      const message = {
        type: "callback",
        action: "onPreview",
        data: {
          args: [previewTemplate.value],
        },
      };

      window.parent.postMessage(JSON.parse(JSON.stringify(message)), "*");

      const frame = document.querySelector(
        "#previewIframe"
      ) as HTMLIFrameElement;

      if (frame !== null && frame.contentWindow !== null) {
        frame.contentWindow.document.open("text/html", "replace");
        frame.contentWindow.document.write(previewTemplate.value);
        frame.contentWindow.document.close();
        previewStore.previewHTML = previewTemplate.value;

        // if (previewStore.enableImageToTextRatio) {
        //   getImageRatioIframe();
        // }
        templateSizingStore.tepmlateSize = tepmlateSize.value;
      }
    } catch (error) {
      console.error(error);
      notificationsStore.createNotification({
        type: "error",
        text: "Error occured while generating preview",
      });
      isLoading.value = false;
    } finally {
      isLoading.value = false;
    }
  };

  const getPreview = async (definition: any, params?: ISetPreviewParams) => {
    const apiPreivewURL = lockedApi.PREVIEW_SAVE;

    let langToSchow = mjmlStore.langToShow || undefined;
    if (params && "langToShow" in params) {
      langToSchow = params.langToShow;
    }

    return await $http.post(apiPreivewURL, {
      definition: definition,
      apiKey: mjmlStore.authorize.apiKey,
      hostname: mjmlStore.authorize.hostname,
      userId: mjmlStore.authorize.userId,
      options: {
        minify: generalStore.htmlMinified,
        desktopFirst: generalStore.htmlDesktopFirst,
        fonts: JSON.stringify(fontStore.fontsWithUrl),
        mergeTags: mergeTagsStore.normalizedMergeTagsForPreview,
        evaluateMergeTags: params?.evaluateMergeTags || false,
        emailServiceProvider: generalStore.emailServiceProvider || "mailchimp",
        variants: {
          evaluate: params?.variants?.evaluate || false,
          enrichWithConditions: params?.variants?.enrichWithConditions || false,
        },
        language: langToSchow,
        smartMergeTags: mergeTagsStore.smartMergeTagsForRequest,
      },
    });
  };

  const setImageRatio = async () => {
    if (previewStore.enableImageToTextRatio) {
      await setPreview();
      let imageContent = 0;

      const wholeTemplate = document.createElement("div");
      wholeTemplate.innerHTML = previewTemplate.value;
      document.body.appendChild(wholeTemplate);

      setTimeout(() => {
        if (wholeTemplate) {
          const templateContent =
            wholeTemplate.clientHeight * mjmlStore.getBodyWidth;
          const allImages = wholeTemplate.querySelectorAll("img");

          allImages.forEach((image) => {
            imageContent += image.clientHeight * image.clientWidth;
          });

          templateSizingStore.imageContent = imageContent;
          templateSizingStore.nonImageContent = templateContent - imageContent;
          wholeTemplate.remove();
        }
      }, 500);
    }
  };

  return {
    isLoading,
    previewTemplate,
    tepmlateSize,
    setPreview,
    getPreview,
    setImageRatio,
    getImageRatioIframe,
  };
};
